import { useCallback, useEffect, useState } from "react";
import type { AdminAppObject, AdminRealObject, AdminSeries } from "@shared";
import { ApiFetchError } from "../api/client";
import * as api from "./adminApi";

interface Props {
  eventId: string;
  series: AdminSeries[];
}

const emptyDraft = { code: "", seriesId: "", targetAppObjectId: "", labelAdmin: "" };

/**
 * Objets réels : le code physique et sa CIBLE CACHÉE.
 *
 * `targetAppObjectId` est la bonne réponse. Elle ne circule que sur les routes
 * /api/admin ; l'app du jeune ne la reçoit jamais, et le calcul de justesse d'un
 * rangement se fait entièrement côté serveur.
 */
export default function RealObjectsPanel({ eventId, series }: Props) {
  const [items, setItems] = useState<AdminRealObject[]>([]);
  const [appObjects, setAppObjects] = useState<AdminAppObject[]>([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [real, targets] = await Promise.all([
        api.listRealObjects(eventId),
        api.listEventAppObjects(eventId),
      ]);
      setItems(real);
      setAppObjects(targets);
    } catch {
      setError("Impossible de charger les codes.");
    }
  }, [eventId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function reset(): void {
    setEditingId(null);
    setDraft(emptyDraft);
  }

  const nameOf = (id: string): string => {
    const found = appObjects.find((o) => o.id === id);
    return found === undefined ? "—" : api.fr(found.nameI18n);
  };

  const seriesNameOf = (id: string): string => {
    const found = series.find((s) => s.id === id);
    return found === undefined ? "—" : api.fr(found.nameI18n);
  };

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    const input = {
      code: draft.code,
      seriesId: draft.seriesId,
      targetAppObjectId: draft.targetAppObjectId,
      labelAdmin: draft.labelAdmin === "" ? null : draft.labelAdmin,
    };

    try {
      if (editingId === null) await api.createRealObject(eventId, input);
      else await api.updateRealObject(editingId, input);
      reset();
      await refresh();
    } catch (err) {
      if (err instanceof ApiFetchError && err.code === "code_taken") {
        setError("Ce code est déjà utilisé dans cet événement. Choisis-en un autre.");
      } else if (err instanceof ApiFetchError && err.code === "target_outside_event") {
        setError("La cible choisie n'appartient pas à cet événement.");
      } else {
        setError("Enregistrement impossible. Le code doit faire 4 chiffres.");
      }
    }
  }

  async function remove(item: AdminRealObject): Promise<void> {
    if (!window.confirm(`Supprimer le code ${item.code} ? Action irréversible.`)) return;
    await api.deleteRealObject(item.id);
    await refresh();
  }

  if (series.length === 0 || appObjects.length === 0) {
    return (
      <section className="panel">
        <h2 className="panel__title">Objets réels (codes)</h2>
        <p className="panel__hint">
          Crée d'abord une série et au moins un objet de l'app : un code doit désigner une bonne
          réponse.
        </p>
      </section>
    );
  }

  return (
    <section className="panel">
      <h2 className="panel__title">Objets réels (codes)</h2>
      <p className="panel__hint">
        Le code à 4 chiffres posé sur l'objet physique, et l'objet de l'app qu'il faut deviner. La
        bonne réponse reste sur le serveur : elle n'est jamais envoyée au jeune.
      </p>

      {error !== null && <p className="form__error">{error}</p>}

      <ul className="list">
        {items.map((item) => (
          <li key={item.id} className="list__item">
            <span className="code-badge">{item.code}</span>

            <div className="list__main list__main--static">
              <span className="list__name">→ {nameOf(item.targetAppObjectId)}</span>
              <span className="list__meta">
                {seriesNameOf(item.seriesId)}
                {item.labelAdmin !== null && ` · ${item.labelAdmin}`}
              </span>
            </div>

            <div className="list__actions">
              <a
                className="button button--small"
                href={api.qrUrl(item.id)}
                download={`qr-${item.code}.png`}
              >
                QR PNG
              </a>
              <button
                type="button"
                className="button button--small"
                onClick={() => {
                  setEditingId(item.id);
                  setDraft({
                    code: item.code,
                    seriesId: item.seriesId,
                    targetAppObjectId: item.targetAppObjectId,
                    labelAdmin: item.labelAdmin ?? "",
                  });
                }}
              >
                Modifier
              </button>
              <button
                type="button"
                className="button button--small button--danger"
                onClick={() => void remove(item)}
              >
                Supprimer
              </button>
            </div>
          </li>
        ))}
        {items.length === 0 && <li className="list__empty">Aucun code dans cet événement.</li>}
      </ul>

      <form className="form" onSubmit={(e) => void submit(e)}>
        <h3 className="form__title">{editingId === null ? "Nouveau code" : "Modifier le code"}</h3>

        <div className="form__row">
          <div className="form__col">
            <label className="form__label" htmlFor="ro-code">
              Code (4 chiffres)
            </label>
            <input
              id="ro-code"
              className="form__input"
              type="text"
              inputMode="numeric"
              value={draft.code}
              maxLength={4}
              placeholder="4821"
              onChange={(e) => {
                setDraft({ ...draft, code: e.target.value.replace(/\D/g, "").slice(0, 4) });
              }}
            />
          </div>
          <div className="form__col">
            <label className="form__label" htmlFor="ro-series">
              Série
            </label>
            <select
              id="ro-series"
              className="form__input"
              value={draft.seriesId}
              onChange={(e) => {
                setDraft({ ...draft, seriesId: e.target.value });
              }}
            >
              <option value="">— choisir —</option>
              {series.map((s) => (
                <option key={s.id} value={s.id}>
                  {api.fr(s.nameI18n)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <label className="form__label" htmlFor="ro-target">
          Bonne réponse (cachée aux jeunes)
        </label>
        <select
          id="ro-target"
          className="form__input"
          value={draft.targetAppObjectId}
          onChange={(e) => {
            setDraft({ ...draft, targetAppObjectId: e.target.value });
          }}
        >
          <option value="">— choisir —</option>
          {appObjects.map((o) => (
            <option key={o.id} value={o.id}>
              {api.fr(o.nameI18n)}
            </option>
          ))}
        </select>

        <label className="form__label" htmlFor="ro-label">
          Note interne (optionnelle, jamais montrée au jeune)
        </label>
        <input
          id="ro-label"
          className="form__input"
          type="text"
          value={draft.labelAdmin}
          placeholder="Collé sous le banc du jardin"
          onChange={(e) => {
            setDraft({ ...draft, labelAdmin: e.target.value });
          }}
        />

        <div className="button-row">
          <button
            type="submit"
            className="button button--primary"
            disabled={
              draft.code.length !== 4 || draft.seriesId === "" || draft.targetAppObjectId === ""
            }
          >
            {editingId === null ? "Créer" : "Enregistrer"}
          </button>
          {editingId !== null && (
            <button type="button" className="button button--ghost" onClick={reset}>
              Annuler
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
